import React, { useEffect, useState } from "react";
import { Users, DollarSign, BookOpen, TrendingUp, ArrowUpRight, Sparkles, Award } from "lucide-react";
import AppLayout from "../../components/AppLayout";
import { api } from "../../api/client";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart } from "recharts";

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [activity, setActivity] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [customers, setCustomers] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/admin/stats").catch(() => null),
            api.get("/admin/recent-activity").catch(() => []),
            api.get("/admin/analytics").catch(() => null),
            api.get("/admin/customers").catch(() => []),
        ]).then(([s, a, an, c]) => {
            setStats(s); setActivity(a || []); setAnalytics(an); setCustomers(c || []);
        });
    }, []);

    const cards = stats ? [
        { label: "Total Students", value: stats.total_students.toLocaleString(), trend: stats.students_trend, icon: Users, color: "lime" },
        { label: "Total Revenue", value: `£${(stats.total_revenue / 1000).toFixed(1)}k`, trend: stats.revenue_trend, icon: DollarSign, color: "grape" },
        { label: "Active Enrollments", value: stats.active_enrollments.toLocaleString(), trend: stats.enrollments_trend, icon: TrendingUp, color: "lime" },
        { label: "Courses Published", value: stats.courses_published, trend: stats.courses_trend, icon: BookOpen, color: "grape" },
        { label: "Corporate Leads", value: stats.leads_total ?? 0, trend: 6.2, icon: Sparkles, color: "lime" },
        { label: "Certificates Issued", value: stats.certificates_issued ?? 0, trend: 9.8, icon: Award, color: "grape" },
    ] : [];

    return (
        <AppLayout searchPlaceholder="Search admin...">
            <div className="mb-8 fade-up">
                <span className="text-xs uppercase tracking-widest text-grape font-semibold">Admin Overview</span>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Mission Control</h1>
                <p className="text-sm text-gray-400 mt-2">Live operational view of Blue Mountain Group LMS.</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                {cards.map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <div key={i} data-testid={`admin-stat-${i}`} className="bg-gray-900 rounded-3xl border border-white/5 p-5 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                            <div className="flex items-center justify-between mb-4">
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${s.color === "lime" ? "bg-lime/15" : "bg-grape/15"}`}>
                                    <Icon className={`w-4.5 h-4.5 ${s.color === "lime" ? "text-lime" : "text-grape"}`} />
                                </div>
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-lime">
                                    <ArrowUpRight className="w-3 h-3" /> {s.trend}%
                                </span>
                            </div>
                            <div className="font-display font-black text-2xl tracking-tight">{s.value}</div>
                            <div className="text-[11px] text-gray-500 mt-1">{s.label}</div>
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="lg:col-span-2 bg-gray-900 rounded-3xl border border-white/5 p-6">
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="font-display font-bold text-xl">Revenue trend</h2>
                        <span className="text-xs text-gray-500">Last 7 months</span>
                    </div>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%" minHeight={250}>
                            <AreaChart data={analytics?.revenue || []}>
                                <defs>
                                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#CFFF50" stopOpacity={0.5} />
                                        <stop offset="100%" stopColor="#CFFF50" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="m" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ background: "#0E0F12", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }} />
                                <Area type="monotone" dataKey="v" stroke="#CFFF50" strokeWidth={2.5} fill="url(#rev)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-gray-900 rounded-3xl border border-white/5 p-6">
                    <h2 className="font-display font-bold text-xl mb-5">Recent activity</h2>
                    <ul className="space-y-4 max-h-72 overflow-y-auto">
                        {activity.map((a, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${a.type === "completion" ? "bg-lime" : a.type === "purchase" ? "bg-grape" : a.type === "certificate" ? "bg-yellow-400" : "bg-blue-400"}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm leading-snug">{a.text}</div>
                                    <div className="text-[11px] text-gray-500 mt-0.5">{a.time}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="bg-gray-900 rounded-3xl border border-white/5 p-6">
                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-display font-bold text-xl">Top customers</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="text-xs uppercase text-gray-500 tracking-widest border-b border-white/5">
                                <th className="text-left font-medium pb-3">Customer</th>
                                <th className="text-left font-medium pb-3">Courses</th>
                                <th className="text-left font-medium pb-3">Total spent</th>
                                <th className="text-left font-medium pb-3">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {customers.slice(0, 6).map((c) => (
                                <tr key={c.id} className="border-b border-white/5 last:border-0">
                                    <td className="py-4">
                                        <div className="flex items-center gap-3">
                                            <img src={c.avatar} alt={c.name} className="w-9 h-9 rounded-full object-cover" />
                                            <div>
                                                <div className="font-semibold text-sm">{c.name}</div>
                                                <div className="text-xs text-gray-500">{c.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-sm">{c.courses_purchased}</td>
                                    <td className="text-sm font-bold">£{(c.spent || 0).toFixed(2)}</td>
                                    <td>
                                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold ${
                                            c.status === "Active" ? "bg-lime/15 text-lime" :
                                            c.status === "Inactive" ? "bg-red-500/15 text-red-400" : "bg-yellow-400/15 text-yellow-400"
                                        }`}>{c.status}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </AppLayout>
    );
}

